import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

export interface BrandContact {
  address: string;
  phone: string;
  phoneDisplay: string;
  email: string;
}

export interface BrandConfig {
  name: string;
  municipality: string;
  logo: string;
  primary: string;
  tagline: string;
  description: string;
  contact: BrandContact;
  copyrightHolder: string;
}

export interface MunicipioThemePatch {
  name?: string;
  municipality?: string;
  logo?: string;
  primary?: string;
  address?: string;
  phone?: string;
  email?: string;
}

const DEFAULT_BRAND: BrandConfig = {
  name: 'CzRM',
  municipality: 'H. Ayuntamiento',
  logo: '',
  primary: '#18181b',
  tagline: 'Trámites y servicios',
  description: 'Portal ciudadano de trámites y servicios en línea.',
  contact: {
    address: '',
    phone: '',
    phoneDisplay: '',
    email: ''
  },
  copyrightHolder: 'CzRM'
};

function brandFromEnvironment(): BrandConfig {
  const fromEnv = (environment as { brand?: Partial<BrandConfig> }).brand;
  if (!fromEnv) {
    return Object.assign({}, DEFAULT_BRAND, {
      contact: Object.assign({}, DEFAULT_BRAND.contact)
    });
  }
  return Object.assign({}, DEFAULT_BRAND, fromEnv, {
    contact: Object.assign({}, DEFAULT_BRAND.contact, fromEnv.contact || {})
  });
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  brand: BrandConfig = brandFromEnvironment();

  get hasLogo(): boolean {
    return !!(this.brand.logo && this.brand.logo.trim());
  }

  apply(): void {
    const root = document.documentElement;
    const primary = this.brand.primary;
    root.style.setProperty('--brand-primary', primary);
    root.style.setProperty('--brand-primary-fg', this.foregroundFor(primary));
    document.title = this.brand.name;
  }

  applyMunicipio(patch: MunicipioThemePatch): void {
    if (patch.name) {
      this.brand.name = patch.name;
      this.brand.copyrightHolder = patch.name;
    }
    if (patch.municipality) {
      this.brand.municipality = patch.municipality;
    }
    if (patch.logo !== undefined) {
      this.brand.logo = patch.logo || '';
    }
    if (patch.primary) {
      this.brand.primary = patch.primary;
    }
    if (patch.address !== undefined) {
      this.brand.contact.address = patch.address || '';
    }
    if (patch.phone !== undefined) {
      this.brand.contact.phone = patch.phone || '';
      this.brand.contact.phoneDisplay = patch.phone || '';
    }
    if (patch.email !== undefined) {
      this.brand.contact.email = patch.email || '';
    }
    this.apply();
  }

  get contactLine(): string {
    const c = this.brand.contact;
    return [c.address, c.phoneDisplay || c.phone].filter(Boolean).join(' · ');
  }

  logoHtml(width: number): string {
    if (this.hasLogo) {
      return `<img src="${this.brand.logo}" width="${width}" alt="${this.brand.name}">`;
    }
    return `<strong style="font-size:16px;letter-spacing:-0.03em">${this.brand.name}</strong>`;
  }

  private foregroundFor(hex: string): string {
    const raw = hex.replace('#', '');
    const normalized = raw.length === 3
      ? raw.split('').map((c) => c + c).join('')
      : raw;
    const n = parseInt(normalized, 16);
    const r = ((n >> 16) & 255) / 255;
    const g = ((n >> 8) & 255) / 255;
    const b = (n & 255) / 255;
    const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
    return luminance > 0.45 ? '#09090b' : '#ffffff';
  }
}
